import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiErrorMessage } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { StarDisplay, StarPicker } from "./StarRating";

function timeAgo(iso) {
    try {
        const d = new Date(iso);
        return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
    } catch { return ""; }
}

export default function ProductReviews({ productId, productSlug }) {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [summary, setSummary] = useState({ average: 0, count: 0, breakdown: {} });
    const [reviews, setReviews] = useState([]);
    const [rating, setRating] = useState(0);
    const [title, setTitle] = useState("");
    const [comment, setComment] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const load = useCallback(async () => {
        const { data } = await api.get(`/products/${productId}/reviews`);
        setSummary(data.summary);
        setReviews(data.reviews || []);
    }, [productId]);
    useEffect(() => { if (productId) load(); }, [productId, load]);

    const submit = async (e) => {
        e.preventDefault();
        if (!user) { toast.info("Sign in to leave a review"); navigate("/login", { state: { from: `/plugins/${productSlug}` } }); return; }
        if (!rating) { toast.error("Pick a rating (1-5 stars)"); return; }
        setSubmitting(true);
        try {
            await api.post(`/products/${productId}/reviews`, { rating, title, comment });
            toast.success("Thanks for your review!");
            setTitle(""); setComment(""); setRating(0);
            await load();
        } catch (err) { toast.error(apiErrorMessage(err)); }
        finally { setSubmitting(false); }
    };

    const total = summary.count || 0;

    return (
        <div className="mt-16 pt-12 border-t border-avx-line" id="reviews" data-testid="product-reviews">
            <div className="label-mono mb-3">/ Reviews</div>
            <h2 className="font-heading font-black text-2xl lg:text-3xl tracking-tighter mb-8">User reviews</h2>

            <div className="grid lg:grid-cols-[280px_1fr] gap-10">
                {/* Summary + breakdown */}
                <div className="panel-elevated p-6 h-fit" data-testid="reviews-summary">
                    <div className="text-5xl font-heading font-black text-avx-text leading-none">{total ? summary.average.toFixed(1) : "—"}</div>
                    <StarDisplay value={summary.average} size={18} showCount={false} className="mt-2" />
                    <div className="text-xs font-mono text-avx-muted mt-1">{total} review{total === 1 ? "" : "s"}</div>
                    <div className="mt-5 space-y-1.5">
                        {[5, 4, 3, 2, 1].map((s) => {
                            const n = summary.breakdown?.[s] || summary.breakdown?.[String(s)] || 0;
                            const pct = total ? Math.round((n / total) * 100) : 0;
                            return (
                                <div key={s} className="flex items-center gap-2 text-xs font-mono text-avx-muted">
                                    <span className="w-3">{s}</span>
                                    <div className="flex-1 h-1.5 rounded-full bg-avx-line overflow-hidden">
                                        <div className="h-full bg-avx-gold" style={{ width: `${pct}%`, backgroundColor: "#F59E0B" }} />
                                    </div>
                                    <span className="w-6 text-right">{n}</span>
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div>
                    {/* Form */}
                    <form onSubmit={submit} className="panel p-5 mb-8" data-testid="review-form">
                        <div className="label-mono mb-3">/ Leave your review</div>
                        <StarPicker value={rating} onChange={setRating} />
                        <input
                            value={title} onChange={(e) => setTitle(e.target.value)}
                            placeholder="Title (optional)" data-testid="review-title-input"
                            className="mt-4 w-full bg-avx-bg border border-avx-line rounded-sm px-3 py-2 text-sm focus:border-avx-blue outline-none"
                        />
                        <textarea
                            value={comment} onChange={(e) => setComment(e.target.value)}
                            placeholder="Tell us about your experience with this plugin…" rows={3} data-testid="review-comment-input"
                            className="mt-3 w-full bg-avx-bg border border-avx-line rounded-sm px-3 py-2 text-sm focus:border-avx-blue outline-none resize-none"
                        />
                        <button type="submit" disabled={submitting} data-testid="review-submit-button"
                            className="mt-3 btn-primary px-5 py-2.5 text-xs font-mono uppercase tracking-[0.2em] rounded-sm disabled:opacity-60">
                            {submitting ? "Submitting…" : "Post review"}
                        </button>
                    </form>

                    {/* List */}
                    {reviews.length === 0 ? (
                        <p className="text-sm text-avx-muted font-mono" data-testid="no-reviews">Be the first to review this plugin.</p>
                    ) : (
                        <div className="space-y-5" data-testid="reviews-list">
                            {reviews.map((r) => (
                                <div key={r.id} className="panel p-5" data-testid={`review-${r.id}`}>
                                    <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
                                        <div className="flex items-center gap-2">
                                            <span className="font-bold text-sm">{r.author_name}</span>
                                            {r.verified && <span className="text-[9px] font-mono uppercase tracking-[0.15em] text-avx-success border border-avx-success/40 bg-avx-success/10 px-1.5 py-0.5 rounded-sm">Verified purchase</span>}
                                        </div>
                                        <span className="text-[10px] font-mono text-avx-muted">{timeAgo(r.created_at)}</span>
                                    </div>
                                    <StarDisplay value={r.rating} size={14} showCount={false} />
                                    {r.title && <div className="font-bold text-sm mt-2">{r.title}</div>}
                                    {r.comment && <p className="text-sm text-avx-muted mt-1 leading-relaxed">{r.comment}</p>}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
